import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/apiClient";

const CONVERSATIONS_POLL_MS = 15000;
const MESSAGES_POLL_MS = 4000;

export function useMessageableUsers(search: string, enabled: boolean) {
  return useQuery({
    queryKey: ["chat-users", search],
    queryFn: async () => (await api.get("/chat/users", { params: { search } })).data.data,
    enabled,
  });
}

export function useConversations(enabled: boolean) {
  return useQuery({
    queryKey: ["chat-conversations"],
    queryFn: async () => (await api.get("/chat/conversations")).data.data,
    enabled,
    refetchInterval: enabled ? CONVERSATIONS_POLL_MS : false,
  });
}

export function useUnreadChatCount() {
  return useQuery({
    queryKey: ["chat-unread-count"],
    queryFn: async () => (await api.get("/chat/conversations/unread-count")).data.data.count as number,
    refetchInterval: CONVERSATIONS_POLL_MS,
  });
}

export function useStartConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => (await api.post("/chat/conversations", { userId })).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat-conversations"] }),
  });
}

export interface CreateGroupConversationInput {
  name: string;
  userIds: string[];
  entityType: "TASK" | "BOARD";
  entityId: string;
}

/** "Discuss" button — starts a named group conversation seeded with a
 * task/project card. */
export function useCreateGroupConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateGroupConversationInput) => (await api.post("/chat/conversations/group", input)).data.data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat-conversations"] });
      qc.invalidateQueries({ queryKey: ["chat-conversations-for-entity"] });
    },
  });
}

export interface DiscussConversationSummary {
  id: string;
  name: string | null;
  participantCount: number;
  createdAt: string;
}

/** Discussions already started from a task/project — lets the "Discuss"
 * button offer "reopen" alongside "start new". */
export function useGroupConversationsForEntity(entityType: "TASK" | "BOARD", entityId: string | undefined) {
  return useQuery({
    queryKey: ["chat-conversations-for-entity", entityType, entityId],
    queryFn: async () =>
      (await api.get<{ data: DiscussConversationSummary[] }>("/chat/conversations/for-entity", { params: { entityType, entityId } })).data.data,
    enabled: !!entityId,
  });
}

/** "+" in an open group thread — adds people beyond the original candidate
 * list, org-wide (same directory as starting a new 1:1). */
export function useAddParticipants(conversationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userIds: string[]) => (await api.post(`/chat/conversations/${conversationId}/participants`, { userIds })).data.data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat-conversations"] });
      qc.invalidateQueries({ queryKey: ["chat-messages", conversationId] });
      qc.invalidateQueries({ queryKey: ["chat-conversation-detail", conversationId] });
      qc.invalidateQueries({ queryKey: ["chat-conversations-for-entity"] });
    },
  });
}

export interface ConversationParticipant {
  userId: string;
  name: string;
}

export interface ConversationDetail {
  id: string;
  name: string | null;
  isGroup: boolean;
  createdById: string | null;
  participants: ConversationParticipant[];
}

/** "Who's in this?" — an open thread's full membership, for a group's
 * member-list/remove popover. */
export function useConversationDetail(conversationId: string | null) {
  return useQuery({
    queryKey: ["chat-conversation-detail", conversationId],
    queryFn: async () => (await api.get<{ data: ConversationDetail }>(`/chat/conversations/${conversationId}`)).data.data,
    enabled: !!conversationId,
  });
}

/** Removing someone from a group — either the creator removing another
 * participant, or a participant removing themselves ("leave"). */
export function useRemoveParticipant(conversationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => api.delete(`/chat/conversations/${conversationId}/participants/${userId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat-conversations"] });
      qc.invalidateQueries({ queryKey: ["chat-conversation-detail", conversationId] });
      qc.invalidateQueries({ queryKey: ["chat-conversations-for-entity"] });
    },
  });
}

export function useConversationMessages(conversationId: string | null) {
  return useQuery({
    queryKey: ["chat-messages", conversationId],
    queryFn: async () => (await api.get(`/chat/conversations/${conversationId}/messages`)).data.data,
    enabled: !!conversationId,
    refetchInterval: conversationId ? MESSAGES_POLL_MS : false,
  });
}

export function useSendChatMessage(conversationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ body, files }: { body?: string; files?: File[] }) => {
      const form = new FormData();
      if (body) form.append("body", body);
      files?.forEach((f) => form.append("files", f));
      return (await api.post(`/chat/conversations/${conversationId}/messages`, form, { headers: { "Content-Type": "multipart/form-data" } })).data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat-messages", conversationId] });
      qc.invalidateQueries({ queryKey: ["chat-conversations"] });
      qc.invalidateQueries({ queryKey: ["chat-unread-count"] });
    },
  });
}

export function useMarkConversationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) => api.post(`/chat/conversations/${conversationId}/read`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat-conversations"] });
      qc.invalidateQueries({ queryKey: ["chat-unread-count"] });
    },
  });
}

export function useEditChatMessage(conversationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ messageId, body }: { messageId: string; body: string }) =>
      (await api.patch(`/chat/conversations/${conversationId}/messages/${messageId}`, { body })).data.data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat-messages", conversationId] });
      qc.invalidateQueries({ queryKey: ["chat-conversations"] });
    },
  });
}

export function useDeleteChatMessage(conversationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (messageId: string) => api.delete(`/chat/conversations/${conversationId}/messages/${messageId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat-messages", conversationId] });
      qc.invalidateQueries({ queryKey: ["chat-conversations"] });
    },
  });
}

/** request 1191: clear several conversations from my inbox at once (each one exactly as "Delete for me" does) */
export function useDeleteConversations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(ids.map((id) => api.delete(`/chat/conversations/${id}`)));
      return { done: results.filter((r) => r.status === "fulfilled").length, failed: results.filter((r) => r.status === "rejected").length };
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["chat-conversations"] });
      qc.invalidateQueries({ queryKey: ["chat-unread-count"] });
    },
  });
}

export function useDeleteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) => api.delete(`/chat/conversations/${conversationId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat-conversations"] });
      qc.invalidateQueries({ queryKey: ["chat-unread-count"] });
    },
  });
}

export async function downloadChatAttachment(attachmentId: string, fileName: string) {
  const res = await api.get(`/chat/attachments/${attachmentId}/download`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
